import React from "react";
import { Reveal } from "@/lib/motion";
import { motion } from "framer-motion";

const IMG_MARBLE =
  "https://images.unsplash.com/photo-1650804068570-7fb2e3dbf888?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxibGFjayUyMG1hcmJsZSUyMHRleHR1cmUlMjBtYWNyb3xlbnwwfHx8fDE3ODU0MDY0MDB8MA&ixlib=rb-4.1.0&q=85";
const IMG_STOPWATCH =
  "https://images.unsplash.com/photo-1704265586142-db3e17d0dea0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxzdG9wd2F0Y2glMjBtYWNybyUyMGRhcmslMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzg1NDA2NDAxfDA&ixlib=rb-4.1.0&q=85";

const methods = [
  {
    n: "01",
    label: "Evidence Based Training",
    body: "Your programming is grounded in sports-science research, not passing trends. Progressive overload, smart recovery, and steady progression, all built around where you are right now.",
    img: IMG_MARBLE,
    span: "lg:col-span-5",
  },
  {
    n: "02",
    label: "Data Driven Nutrition",
    body: "Calories and macros are set to your body and your goal, then adjusted against real feedback. No fads, no crash diets, just a plan that scales with you.",
    img: null,
    span: "lg:col-span-7",
  },
  {
    n: "03",
    label: "Weekly Personalised Check-ins",
    body: "Every week we connect to review what's working, what isn't, and what changes. You're never left guessing, because there's always a real person in your corner.",
    img: IMG_STOPWATCH,
    span: "lg:col-span-12",
  },
];

const Card = ({ m, wide }) => (
  <motion.div
    whileHover={{ y: -6 }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    className={`group glass rounded-2xl overflow-hidden relative flex ${
      wide ? "flex-col md:flex-row" : "flex-col"
    } hover:border-white/25`}
    data-testid={`method-card-${m.n}`}
  >
    {m.img && (
      <div className={`relative overflow-hidden ${wide ? "md:w-1/2 h-64 md:h-auto" : "h-56"}`}>
        <img
          src={m.img}
          alt=""
          className="h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:opacity-80 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
      </div>
    )}
    <div className={`p-8 md:p-12 flex flex-col justify-between ${wide ? "md:w-1/2" : "flex-1"}`}>
      <span className="font-cormorant italic text-3xl text-white/20">{m.n}</span>
      <div className="mt-8">
        <h3 className="font-syne text-2xl md:text-3xl font-bold tracking-tight">{m.label}</h3>
        <p className="mt-4 text-white/50 leading-relaxed font-light max-w-md">{m.body}</p>
      </div>
    </div>
    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-[radial-gradient(600px_circle_at_50%_0%,rgba(255,255,255,0.06),transparent_70%)]" />
  </motion.div>
);

export const Method = () => {
  return (
    <section
      id="method"
      data-testid="method-section"
      className="relative py-28 md:py-40 max-w-[1400px] mx-auto px-6 md:px-12"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
        <div>
          <Reveal>
            <span className="text-xs uppercase tracking-[0.28em] text-white/40">The Method</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-syne font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98] max-w-2xl">
              Three Pillars. <span className="font-cormorant italic font-light text-white/60">One System.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="max-w-xs text-white/45 font-light leading-relaxed md:text-right">
            What separates HANCOCK from an app or a template is a real system,
            and a real human behind it.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-5">
          <Card m={methods[0]} />
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <Card m={methods[1]} />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-12">
          <Card m={methods[2]} wide />
        </Reveal>
      </div>
    </section>
  );
};
